import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { UploadCloud, FileText, CheckCircle, Loader2, AlertCircle, RotateCcw, X, Plus } from 'lucide-react';
import { uploadReimbursementFile } from '../../../Services/APIs/reimbursement';
import { UPLOAD_RULES, uploadKind, fileProblem, formatFileSize } from './reimbursementStatus';
import { uploadsReducer, uploadsBusy, slotsToShow } from './formHelpers';

// Uploads for one form. Each file goes to S3 the moment it is picked (ADR-0003), with progress, and
// can be cancelled, replaced, removed or retried. A file the slot cannot take (wrong type, too big,
// empty) is refused before uploading and never replaces one already uploaded. `type` is the upload
// endpoint's 'prescription' | 'claim'.
export const useUploads = ({ type }) => {
    const [uploads, dispatch] = useReducer(uploadsReducer, {});
    const attempts = useRef(0);
    const controllers = useRef({});

    useEffect(() => () => Object.values(controllers.current).forEach((controller) => controller.abort()), []);

    const send = useCallback(async (slot, file) => {
        controllers.current[slot]?.abort();
        attempts.current += 1;
        const attempt = attempts.current;
        const controller = new AbortController();
        controllers.current[slot] = controller;
        dispatch({ type: 'start', slot, file, attempt });
        try {
            const response = await uploadReimbursementFile(file, { type, field: slot }, {
                signal: controller.signal,
                onUploadProgress: (event) => {
                    if (event.total) dispatch({ type: 'progress', slot, attempt, progress: (event.loaded / event.total) * 100 });
                },
            });
            const url = response.data?.data?.url;
            if (!url) throw new Error('The upload returned no file.');
            dispatch({ type: 'done', slot, attempt, url });
        } catch (err) {
            // Cancelled or replaced: the reducer already moved on.
            if (controller.signal.aborted) return;
            dispatch({ type: 'fail', slot, attempt, error: err?.response?.data?.message || 'Upload failed. Please try again.' });
        } finally {
            if (controllers.current[slot] === controller) delete controllers.current[slot];
        }
    }, [type]);

    const pick = useCallback((slot, file) => {
        if (!file) return;
        const problem = fileProblem(file, uploadKind(slot));
        if (problem) dispatch({ type: 'invalid', slot, error: problem });
        else send(slot, file);
    }, [send]);

    // Also cancels an upload in flight.
    const remove = useCallback((slot) => {
        controllers.current[slot]?.abort();
        delete controllers.current[slot];
        dispatch({ type: 'remove', slot });
    }, []);

    const retry = useCallback((slot) => {
        const file = uploads[slot]?.file;
        if (file) send(slot, file);
    }, [send, uploads]);

    // The props an UploadField needs for one slot.
    const bind = (slot) => ({
        slot,
        upload: uploads[slot] || null,
        onPick: (file) => pick(slot, file),
        onRemove: () => remove(slot),
        onRetry: () => retry(slot),
    });

    return { uploads, pick, remove, retry, bind, busy: uploadsBusy(uploads) };
};

// A local preview of a picked photo (never the uploaded copy, which is private).
const usePreview = (file) => {
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        if (!file || !/^image\/(jpe?g|png)$/.test(file.type || '')) {
            setPreview(null);
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);
    return preview;
};

const SMALL_BUTTON = 'inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors';

// One upload slot. `id` is what focusFirstError focuses; `error` is the form's validation message,
// shown unless the upload itself has a more specific one.
export const UploadField = ({ id, slot, label, required = false, hint, upload, error, onPick, onRemove, onRetry, testId }) => {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const rules = UPLOAD_RULES[uploadKind(slot)];
    const file = upload?.file || null;
    const preview = usePreview(file);
    const message = upload?.error || error || '';
    const errorId = `${id}-error`;
    const status = upload?.status;

    const choose = () => inputRef.current?.click();
    const onDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        onPick(event.dataTransfer?.files?.[0]);
    };

    return (
        <div
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
        >
            <p className="text-xs font-semibold text-portal-ink mb-1.5">
                {label}{required && <span className="text-red-600"> *</span>}
            </p>
            {hint && <p className="text-xs text-portal-sub -mt-1 mb-1.5">{hint}</p>}

            {file ? (
                <div className={`rounded-xl border p-2.5 bg-white ${message && status !== 'done' ? 'border-red-300' : dragging ? 'border-portal-purple bg-portal-purple-light' : 'border-portal-border'}`}>
                    <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-portal-bg border border-portal-border flex items-center justify-center flex-shrink-0">
                            {preview
                                ? <img src={preview} alt="" className="w-full h-full object-cover" />
                                : <FileText className="w-5 h-5 text-portal-purple" aria-hidden="true" />}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-portal-ink truncate">{file.name}</p>
                            <p className="text-xs text-portal-sub">{formatFileSize(file.size)}</p>
                            {status === 'uploading' && (
                                <>
                                    <div
                                        role="progressbar"
                                        aria-label={`Uploading ${label}`}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={upload.progress}
                                        className="h-1.5 bg-portal-purple-light rounded-full mt-1.5 overflow-hidden"
                                    >
                                        <div className="h-full bg-portal-purple rounded-full transition-all" style={{ width: `${upload.progress}%` }} />
                                    </div>
                                    <p className="flex items-center gap-1.5 text-xs text-portal-sub mt-1">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Uploading… {upload.progress}%
                                    </p>
                                </>
                            )}
                            {status === 'done' && (
                                <p className="flex items-center gap-1 text-xs font-semibold text-portal-green-ink mt-1">
                                    <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" /> Uploaded
                                </p>
                            )}
                        </div>
                    </div>
                    {/* Under the file, full width, so the buttons stay on one line on a phone. */}
                    <div className="flex flex-wrap gap-2 mt-2.5">
                        {status === 'uploading' && (
                            <button type="button" id={id} onClick={onRemove} aria-label={`Cancel upload of ${label}`} className={`${SMALL_BUTTON} text-portal-ink border-portal-border hover:bg-portal-bg`}>
                                <X className="w-3.5 h-3.5" aria-hidden="true" /> Cancel
                            </button>
                        )}
                        {status === 'error' && (
                            <button type="button" id={id} onClick={onRetry} aria-label={`Retry ${label}`} className={`${SMALL_BUTTON} text-portal-purple-dark border-portal-purple-border bg-portal-purple-light hover:bg-portal-purple-border`}>
                                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Retry
                            </button>
                        )}
                        {status !== 'uploading' && (
                            <>
                                <button type="button" id={status === 'error' ? undefined : id} onClick={choose} aria-label={`Replace ${label}`} className={`${SMALL_BUTTON} text-portal-purple-dark border-portal-purple-border hover:bg-portal-purple-light`}>
                                    <UploadCloud className="w-3.5 h-3.5" aria-hidden="true" /> Replace
                                </button>
                                <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className={`${SMALL_BUTTON} text-portal-sub border-portal-border hover:text-red-700 hover:border-red-200 hover:bg-red-50`}>
                                    <X className="w-3.5 h-3.5" aria-hidden="true" /> Remove
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    id={id}
                    onClick={choose}
                    aria-describedby={message ? errorId : undefined}
                    className={`w-full flex items-center gap-3 text-left rounded-xl border-2 border-dashed px-3 py-3 transition-colors ${dragging ? 'border-portal-purple bg-portal-purple-light' : message ? 'border-red-300 bg-red-50' : 'border-portal-purple-border bg-portal-card hover:bg-portal-purple-light'}`}
                >
                    <span className="w-10 h-10 rounded-full bg-portal-purple-light text-portal-purple flex items-center justify-center flex-shrink-0">
                        <UploadCloud className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                        <span className="block text-sm font-semibold text-portal-purple-dark">Choose {uploadKind(slot) === 'image' ? 'photo' : 'file'}<span className="sr-only"> for {label}</span></span>
                        <span className="block text-xs text-portal-sub">or drag it here · {rules.hint}</span>
                    </span>
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={rules.accept}
                className="hidden"
                data-testid={testId}
                aria-label={label}
                onChange={(event) => {
                    const picked = event.target.files?.[0];
                    // Lets the member pick the same file again; cancelling the picker changes nothing.
                    event.target.value = '';
                    if (picked) onPick(picked);
                }}
            />

            {message && (
                <p id={errorId} role="alert" className="flex items-start gap-1.5 text-xs text-red-700 mt-1.5">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" /> {message}
                </p>
            )}
        </div>
    );
};

// A group of upload slots for an array field (optional X-rays, supporting documents, re-uploaded
// pre-op X-rays): the first slot, every slot in use, and "Add another" up to the last one.
// `onPick(slot)` runs before a file is picked into a slot (e.g. to clear the form's error for it).
export const UploadSlots = ({ slots, uploads, bind, labelFor, addLabel, idFor, testIdFor, errors = {}, requiredFirst = false, onPick }) => {
    const [requested, setRequested] = useState(1);
    const shown = slotsToShow(slots, uploads, requested);
    return (
        <div className="space-y-3">
            {slots.slice(0, shown).map((slot, index) => {
                const props = bind(slot);
                return (
                    <UploadField
                        key={slot}
                        id={idFor(slot)}
                        label={labelFor(index)}
                        required={requiredFirst && index === 0}
                        testId={testIdFor(slot)}
                        error={errors[slot]}
                        {...props}
                        onPick={(file) => { onPick?.(slot); props.onPick(file); }}
                    />
                );
            })}
            {shown < slots.length && (
                <button
                    type="button"
                    onClick={() => setRequested(shown + 1)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-portal-purple-dark border border-portal-purple-border rounded-lg hover:bg-portal-purple-light transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" aria-hidden="true" /> {addLabel}
                </button>
            )}
        </div>
    );
};

export default UploadField;
